import moment from "moment";

/*
 * Shared fee-ledger maths for the student ledger screen and its printed report.
 *
 * Kept in one place so the printed figures can never drift from the ones on screen -
 * they are the numbers the accounts staff reconcile against.
 *
 * Background on the data model:
 *   tblADLedger  one row per fee head per posting. Unpaid amounts are carried into the
 *                next month's row as Arrears, so the SAME debt appears on several rows.
 *                Net outstanding therefore uses the latest row per fee code; cash
 *                received sums every row, because settled months keep their own rows.
 *   tblChalanHead  one row per printed voucher. Regenerating a month soft-deletes the
 *                old voucher and issues a new one, so a student can have several
 *                vouchers for the same month (only the live one is returned by the API).
 *   Reference_ID_Type  Monthly | Installment | Advance | Admission - how the line arose.
 */

function parseMoney(v) {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(n) {
  return parseMoney(n).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/** One row per fee code: latest PostingDate (then highest ID). Avoids double-counting when arrears are rolled into a later month's receivable. */
function getLatestRowPerFeeCode(rows) {
  if (!rows || rows.length === 0) return [];
  const byFee = new Map();
  for (const item of rows) {
    const code = String(item.FeeCode ?? "").trim() || "_";
    const existing = byFee.get(code);
    const t = item.PostingDate ? new Date(item.PostingDate).getTime() : 0;
    const id = item.ID != null ? Number(item.ID) : 0;
    if (!existing) {
      byFee.set(code, item);
      continue;
    }
    const et = existing.PostingDate ? new Date(existing.PostingDate).getTime() : 0;
    const exid = existing.ID != null ? Number(existing.ID) : 0;
    if (t > et || (t === et && id > exid)) {
      byFee.set(code, item);
    }
  }
  return Array.from(byFee.values());
}

function sumLedgerRows(rows) {
  return rows.reduce(
    (acc, item) => ({
      receiveable: acc.receiveable + parseMoney(item.ReceiveableFee),
      received: acc.received + parseMoney(item.ReceivedAmount),
      pending: acc.pending + parseMoney(item.PendingFee),
      arrears: acc.arrears + parseMoney(item.Arrears),
      feePosted: acc.feePosted + parseMoney(item.Fee_Posted),
      concession: acc.concession + parseMoney(item.ConcessionAmount),
    }),
    { receiveable: 0, received: 0, pending: 0, arrears: 0, feePosted: 0, concession: 0 }
  );
}

function computeLedgerTotals(rows) {
  const latestRows = getLatestRowPerFeeCode(rows);
  const current = sumLedgerRows(latestRows);
  const lifetime = sumLedgerRows(rows);
  return {
    /** Unpaid balance per fee head (latest row only — arrears not double-counted). */
    outstanding: current.pending,
    currentArrears: current.arrears,
    /** Cash collected: sum every ledger line (paid months stay on older rows). */
    totalReceived: lifetime.received,
    lifetimeReceivablePosted: lifetime.receiveable,
  };
}

/** Due amount from challan head — latest, or matched by challan no / month-year when filtering. */
function getChallanReceivableAmount(challanList, options = {}) {
  if (!challanList || challanList.length === 0) return null;

  const challanNo = (options.challanNo || "").trim();
  if (challanNo) {
    const ch = challanList.find(
      (c) => (c.Challan_No || "").trim() === challanNo
    );
    return ch ? getChallanDueAmount(ch) : null;
  }

  const month = options.month ? parseInt(options.month, 10) : 0;
  const year = options.year ? parseInt(options.year, 10) : 0;
  if (month > 0 && year > 0) {
    const ch = challanList.find(
      (c) => Number(c.Month) === month && Number(c.Year) === year
    );
    return ch ? getChallanDueAmount(ch) : null;
  }

  return getChallanDueAmount(challanList[0]);
}

/** Due amount on challan head (before/after due date), same rule as bank scroll posting. */
function getChallanDueAmount(ch, asOf = new Date()) {
  const due = parseMoney(ch.Due_Date_Amount);
  const after = parseMoney(ch.After_Due_Date_Amount);
  if (
    ch.Due_Date &&
    moment(asOf).isAfter(moment(ch.Due_Date), "day") &&
    after > 0
  ) {
    return after;
  }
  return due > 0 ? due : after;
}

/**
 * Rows for the most recent fee posting (latest challan if present, else latest calendar month).
 * Used for "current challan" breakdown — not deduped per fee code (all lines belong to one posting).
 */
function getLatestPostingSlice(rows) {
  if (!rows || rows.length === 0) {
    return { rows: [], monthLabel: "", challanNo: "" };
  }

  let latest = rows[0];
  for (const r of rows) {
    const t = r.PostingDate ? new Date(r.PostingDate).getTime() : 0;
    const lt = latest.PostingDate ? new Date(latest.PostingDate).getTime() : 0;
    const id = r.ID != null ? Number(r.ID) : 0;
    const lid = latest.ID != null ? Number(latest.ID) : 0;
    if (t > lt || (t === lt && id > lid)) {
      latest = r;
    }
  }

  const challanNo = (latest.Challan_No || "").trim();
  const slice = challanNo
    ? rows.filter((r) => (r.Challan_No || "").trim() === challanNo)
    : rows.filter((r) => {
        if (!r.PostingDate || !latest.PostingDate) return false;
        const m = moment(r.PostingDate);
        const lm = moment(latest.PostingDate);
        return m.month() === lm.month() && m.year() === lm.year();
      });

  return {
    rows: slice,
    monthLabel: latest.PostingDate
      ? moment(latest.PostingDate).format("MMM YYYY")
      : "",
    challanNo,
  };
}

/** Cash actually received against one voucher, taken from its ledger lines. */
export function getChallanReceivedFromLedger(ledgerRows, challanNo) {
  const cn = (challanNo || "").trim();
  if (!cn || !ledgerRows || ledgerRows.length === 0) return 0;
  return ledgerRows
    .filter((r) => (r.Challan_No || "").trim() === cn)
    .reduce((sum, r) => sum + parseMoney(r.ReceivedAmount), 0);
}

/**
 * Settlement state of a voucher, derived from money rather than the Is_Paid flag alone.
 * The flag is only set on a full settlement, so a part payment otherwise reads as Unpaid
 * with nothing received against it.
 */
export function getChallanSettlement(ch, ledgerRows) {
  const due = getChallanDueAmount(ch);
  const received = getChallanReceivedFromLedger(ledgerRows, ch.Challan_No);
  const balance = due - received;
  let status = "Unpaid";
  if (ch.Is_Paid || (due > 0 && balance <= 0.005)) {
    status = "Paid";
  } else if (received > 0) {
    status = "Partial";
  }
  return { due, received, balance: balance > 0 ? balance : 0, status };
}

/**
 * Group ledger lines into the vouchers that produced them.
 *
 * The fee system works voucher by voucher: whatever is left unpaid on one voucher is
 * re-billed on the next as Arrears on each fee line. Read as a flat list of rows that
 * is impossible to follow, because the same debt reappears every month. Grouped this
 * way each voucher shows what was newly charged, what was carried in from before, what
 * was collected, and what therefore rolls on to the next one.
 *
 * Returned newest first, so the current position is at the top.
 */
export function groupLedgerByChallan(rows, challans) {
  const groups = new Map();

  for (const row of rows || []) {
    const key = (row.Challan_No || "").trim() || "(no voucher)";
    if (!groups.has(key)) {
      groups.set(key, {
        challanNo: key,
        rows: [],
        postingDate: row.PostingDate || null,
        referenceType: row.Reference_ID_Type || "",
      });
    }
    const g = groups.get(key);
    g.rows.push(row);
    if (row.PostingDate && (!g.postingDate || new Date(row.PostingDate) < new Date(g.postingDate))) {
      g.postingDate = row.PostingDate;
    }
    if (!g.referenceType && row.Reference_ID_Type) {
      g.referenceType = row.Reference_ID_Type;
    }
  }

  const list = Array.from(groups.values()).map((g) => {
    const sums = sumLedgerRows(g.rows);
    const challan = (challans || []).find(
      (c) => (c.Challan_No || "").trim() === g.challanNo
    );
    return {
      ...g,
      sums,
      challan: challan || null,
      settlement: challan
        ? getChallanSettlement(challan, rows)
        : {
            due: sums.receiveable,
            received: sums.received,
            balance: sums.pending,
            status:
              sums.pending <= 0.005 && sums.received > 0
                ? "Paid"
                : sums.received > 0
                  ? "Partial"
                  : "Unpaid",
          },
      /** Newly billed this voucher, before arrears and after concession. */
      currentCharges: sums.feePosted - sums.concession,
      monthLabel: g.postingDate ? moment(g.postingDate).format("MMMM YYYY") : "",
    };
  });

  list.sort((a, b) => {
    const at = a.postingDate ? new Date(a.postingDate).getTime() : 0;
    const bt = b.postingDate ? new Date(b.postingDate).getTime() : 0;
    if (bt !== at) return bt - at;
    return String(b.challanNo).localeCompare(String(a.challanNo));
  });

  return list;
}

export {
  parseMoney,
  formatMoney,
  getLatestRowPerFeeCode,
  sumLedgerRows,
  computeLedgerTotals,
  getChallanReceivableAmount,
  getChallanDueAmount,
  getLatestPostingSlice,
};
